import { useSupportSheet } from "../../components/SupportSheet";
import { memo, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
  SuccessIcon,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { useStore } from "../../store/globalState";
import { SEND_ROUTES, TITLE_COLOR } from "../../constants";
import { LegalModal } from "../../components/legal/LegalModal";
import { Popup } from "../../components/Popup";
import { useSendLiveReceipt } from "../../gallery/sendLiveMock";
import { formatHeroAmount } from "../../utils";
import { CheckIcon, CopyIcon, LimitInfoIcon, StatusCircleIcon, StatusFailedIcon, StatusPendingIcon } from "../../assets/icons";
import {
  shareOrDownloadBlob,
  shouldOfferNativeShare,
} from "../../utils/shareReceipt";
import { buildReceiptPdf } from "../../utils/receiptPdf";
import {
  buildReceiptRows,
  referenceLabel,
  splitVisibleReferences,
} from "../../utils/receiptRows";
import {
  STATUS_TONE_COLOR,
  toStatusPresentation,
  type StatusTone,
} from "../../utils/statusPresentation";
import "../../styles/legal.style.css";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


const statusIcon = (tone: StatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

export const ReceiptSummaryPage = memo(() => {
  const { t, i18n } = useTranslation(["send", "translation"]);
  const { theme } = useStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("send");
  const location = useLocation();
  const { operationId: operationIdParam } = useParams<{ operationId: string }>();

  const colorConfig = theme.whiteView;
  const {
    bodyCopy,
    dropzoneColors,
    title,
    footerColor,
    background,
    backgroundBtn,
    textColorBtn,
  } = colorConfig;
  const [showTransactionPolicyModal, setShowTransactionPolicyModal] =
    useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [copiedType, setCopiedType] = useState<string | null>(null);
  const sharingRef = useRef(false);
  const offerNativeShare = useMemo(
    () => shouldOfferNativeShare("application/pdf"),
    []
  );
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "en").split("-")[0];

  const operation = useSendLiveReceipt(operationIdParam, location.pathname);
  const receiptOp = useMemo(
    () => ({
      ...operation,
      recipient: {
        name: operation.recipient?.canonicalName ?? undefined,
      },
    }),
    [operation]
  );

  const handleShare = async () => {
    if (sharingRef.current) return;
    sharingRef.current = true;
    try {
      const { prominent } = splitVisibleReferences(operation.references);
      const blob = await buildReceiptPdf({
        operation: receiptOp,
        rows: buildReceiptRows(receiptOp, t, { lang }),
        prominentRefs: prominent,
        strings: {
          documentTitle: t("receipt.documentTitle"),
          youTransferred: t("receipt.youTransferred"),
          transactionId: t("receipt.transactionId"),
          footerNote: t("receipt.footerNote"),
        },
        t,
        lang,
      });
      await shareOrDownloadBlob({
        blob,
        fileName: t("receipt.shareFileName", {
          id: operation.operationId ?? "recibo",
        }),
        title: t("receipt.shareTitle"),
        mediaType: "application/pdf",
      });
    } finally {
      sharingRef.current = false;
    }
  };

  const source = operation.sourceCurrency;
  const { tone } = toStatusPresentation(operation.status);
  const statusColor = STATUS_TONE_COLOR[tone];
  const { prefix, suffix, intPart: sendInt, decPart: sendDec } = formatHeroAmount(
    operation.sendAmount,
    source
  );
  const { prominent: prominentRefs } = splitVisibleReferences(
    operation.references
  );
  const rows = buildReceiptRows(receiptOp, t, { lang });

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="need-help-header receipt-summary-section"
      >
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(SEND_ROUTES.HOME)}
            onIconClick={openSupport}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="receipt-summary-content">
          <section className="receipt-summary-container">
            <section className="receipt-summary-scroll">
              <ScrollFadeIndicator color={bodyCopy} />
              <section className="summary-section">
                <section className="receipt-card-capture">
                  <section className="summary-title">
                    <BodyCopy textColor={bodyCopy}>
                      {t("receipt.youTransferred")}
                    </BodyCopy>
                    <GradientText as="h1">
                      {prefix}{sendInt}.<label>{sendDec}</label>
                      {suffix ? (
                        <span className="receipt-hero-currency">{suffix}</span>
                      ) : null}
                    </GradientText>
                    <BodyCopy
                      className="receipt-trx-caption"
                      textColor={bodyCopy}
                      aria-label={t("receipt.transactionId")}
                    >
                      {operation.operationId.slice(0, 8).toUpperCase()}
                    </BodyCopy>
                  </section>
                  {prominentRefs.map((ref) => (
                    <section
                      key={`${ref.type}:${ref.value}`}
                      className="receipt-reference-hero"
                      style={{ borderColor: "#eeeeee" }}
                    >
                      <BodyCopy textColor={bodyCopy}>
                        {referenceLabel(ref.type, ref.label, t, lang)}
                      </BodyCopy>
                      <div className="receipt-reference-value-wrap">
                        <p
                          className="receipt-reference-value"
                          style={{ color: TITLE_COLOR }}
                        >
                          {ref.value}
                        </p>
                        <button
                          type="button"
                          className="receipt-reference-copy"
                          style={{ color: bodyCopy }}
                          aria-label={
                            copiedType === ref.type
                              ? t("receipt.copied")
                              : t("receipt.copy")
                          }
                          onClick={() => {
                            void navigator.clipboard.writeText(ref.value);
                            setCopiedType(ref.type);
                            window.setTimeout(() => {
                              setCopiedType((current) =>
                                current === ref.type ? null : current
                              );
                            }, 2000);
                          }}
                        >
                          {copiedType === ref.type ? (
                            <CheckIcon size={16} />
                          ) : (
                            <CopyIcon size={16} />
                          )}
                        </button>
                      </div>
                    </section>
                  ))}
                  <section className="summary-info">
                    {rows.map((row) => (
                      <SummaryInfoItem
                        key={row.id}
                        status={row.label}
                        value={row.value}
                        isBold={row.emphasis}
                        statusColor={
                          row.id === "status" ? statusColor : undefined
                        }
                        icon={
                          row.id === "status"
                            ? statusIcon(tone, statusColor)
                            : undefined
                        }
                      />
                    ))}
                  </section>
                </section>
              </section>
              <section className="receipt-summary-disclaimers">
                <BodyCopy className="footer-text" textColor={footerColor}>
                  {t("receipt.footerNote")}
                </BodyCopy>
                <BodyCopy className="footer-text">
                  <GradientText
                    as="button"
                    type="button"
                    className="receipt-cancel-policy"
                    textColor={title}
                    onClick={() => setShowTransactionPolicyModal(true)}
                  >
                    {t("receipt.cancelationPolicy")}
                  </GradientText>
                </BodyCopy>
              </section>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section transactions-footer">
            {operation.cancellation?.allowed && (
              <BodyCopy className="footer-text">
                <button
                  type="button"
                  className="receipt-cancel-transfer"
                  style={{ color: TITLE_COLOR }}
                  onClick={() => setShowConfirm(true)}
                >
                  {t("receipt.cancelTransfer")}
                </button>
              </BodyCopy>
            )}
            <section className="receipt-share-cta">
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                onClick={() => void handleShare()}
              >
                {offerNativeShare
                  ? t("receipt.shareReceipt")
                  : t("receipt.downloadReceipt")}
              </Button>
            </section>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <LegalModal
        doc="transactionPolicy"
        visible={showTransactionPolicyModal}
        onClose={() => setShowTransactionPolicyModal(false)}
      />
      <Popup visible={showConfirm} onClose={() => setShowConfirm(false)} showClose={false}>
        <section className="cancel-confirm-slot">
          <LimitInfoIcon
            className="cancel-confirm-icon"
            size={60}
            colors={[TITLE_COLOR, TITLE_COLOR]}
          />
          <Title
            title={t("receipt.cancelConfirmTitle")}
            align="center"
            color={TITLE_COLOR}
          />
          <BodyCopy textColor={bodyCopy}>
            {t(
              operation.cancellation?.mode === "void"
                ? "receipt.cancelConfirmMessageVoid"
                : "receipt.cancelConfirmMessageRequest"
            )}
          </BodyCopy>
          <div className="cancel-confirm-actions">
            <Button
              size="large"
              variant="outline"
              background={backgroundBtn}
              color={TITLE_COLOR}
              style={{ backgroundColor: background }}
              onClick={() => setShowConfirm(false)}
            >
              {t("receipt.cancelConfirmYes")}
            </Button>
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => setShowConfirm(false)}
            >
              {t("receipt.cancelConfirmNo")}
            </Button>
          </div>
        </section>
      </Popup>
      {supportSheet}
    </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  statusColor?: string;
  status: string;
  value: string | React.ReactNode;
  icon?: React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  statusColor,
  icon,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p>{status}</p>
    <p
      className={icon ? "summary-info-status" : undefined}
      style={{
        ...(statusColor ? { color: statusColor } : undefined),
        whiteSpace: "pre-line",
        textAlign: "right",
      }}
    >
      {icon}
      {value}
    </p>
  </section>
);
